import React, { useState, useRef } from 'react';

const TextInput = ({ 
  style, 
  value, 
  onChangeText, 
  placeholder,
  placeholderTextColor = '#999',
  multiline = false,
  numberOfLines = 1,
  editable = true,
  secureTextEntry = false,
  keyboardType = 'default',
  autoCapitalize = 'sentences',
  autoCorrect = true,
  onFocus,
  onBlur,
  onSubmitEditing,
  returnKeyType = 'default',
  ...props 
}) => {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef();
  
  const flattenStyle = (rnStyle) => {
    if (!rnStyle) return {};
    const cssStyle = { ...rnStyle };
    if (typeof cssStyle.flex === 'number') {
      cssStyle.flexGrow = cssStyle.flex;
      cssStyle.flexShrink = 1;
      cssStyle.flexBasis = '0%';
      delete cssStyle.flex;
    }
    return cssStyle;
  };

  // Default TextInput styling — applied once underneath all user styles
  // The field of the Strike AI style template (docs/STRIKE_STYLE.md, section 6)
  const defaultStyle = {
    border: '1px solid var(--input)',
    borderRadius: 'var(--radius)',
    padding: '4px 6px',
    fontSize: 12,
    fontFamily: 'inherit',
    outline: 'none',
    backgroundColor: editable ? 'var(--background)' : 'var(--muted)',
    color: editable ? 'var(--foreground)' : 'var(--text-secondary)',
    boxSizing: 'border-box'
  };

  if (focused) {
    defaultStyle.borderColor = 'var(--ring)';
    defaultStyle.boxShadow = 'var(--focus-ring)';
  }

  const mergedUserStyle = Array.isArray(style)
    ? style.reduce((acc, s) => ({ ...acc, ...flattenStyle(s) }), {})
    : flattenStyle(style);

  const processedStyle = { ...defaultStyle, ...mergedUserStyle };
  
  const handleFocus = (e) => {
    setFocused(true);
    onFocus?.(e);
  };
  
  const handleBlur = (e) => {
    setFocused(false);
    onBlur?.(e);
  };
  
  const handleChange = (e) => {
    onChangeText?.(e.target.value);
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !multiline) {
      onSubmitEditing?.(e);
    }
  };
  
  // Convert keyboardType to input type
  const getInputType = () => {
    if (secureTextEntry) return 'password';
    switch (keyboardType) {
      case 'numeric':
      case 'number-pad':
        return 'number';
      case 'email-address':
        return 'email';
      case 'phone-pad':
        return 'tel';
      case 'url':
        return 'url';
      default:
        return 'text';
    }
  };
  
  const commonProps = {
    ref: inputRef,
    style: processedStyle,
    value: value || '',
    onChange: handleChange,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onKeyPress: handleKeyPress,
    placeholder: placeholder,
    disabled: !editable,
    autoCapitalize: autoCapitalize,
    autoCorrect: autoCorrect ? 'on' : 'off',
    ...props
  };
  
  if (multiline) {
    return (
      <textarea
        {...commonProps}
        rows={numberOfLines}
        style={{
          ...processedStyle,
          resize: 'vertical',
          fontFamily: 'inherit'
        }}
      />
    );
  }
  
  return (
    <input
      {...commonProps}
      type={getInputType()}
      style={{
        ...processedStyle,
        '::placeholder': { color: placeholderTextColor }
      }}
    />
  );
};

export default TextInput;